/**
 * Death and ownership screens (spec §9.2; plan §6 M2-18): the rebuild report ("Rebuilt at <station>: rebuild fee
 * 17,400 added to your lease; your cargo is in your wreck") and the full-screen ownership moment.
 * M6 (D-459 bug 5): a death in a flown fleet ship gets its own line: an uninsured one is gone for good and the pilot
 * is back in the lease ship (no rebuild fee to report); an insured one is rebuilt at the (halved) fee as before.
 */
import type { Career } from '../../career/types'
import { stationIndexOf } from '../../data/m2'
import type { M2Data } from '../../data/m2Schemas'
import type { DeathReport } from '../contracts'
import { formatCr, formatCredits, formatDuration, formatTonnes } from '../format'

export interface DeathModel {
  title: string
  /** The main line. */
  headline: string
  /** Further lines: repo fee, insurer debt, the cargo estimate. */
  details: string[]
}

export function deathModel(data: Pick<M2Data, 'stations'>, report: DeathReport): DeathModel {
  const i = stationIndexOf(data, report.station)
  const where = i < 0 ? report.station : data.stations.stations[i].name
  const fee = formatCredits(report.fee)
  const how = report.addedToPrincipal ? 'added to your lease' : 'paid from your wallet'
  const cargo = report.cargoValue > 0 ? 'your cargo is in your wreck' : 'your hold was empty'
  const details: string[] = []
  if (report.repoFee > 0) details.push(`Repo fee: ${formatCr(report.repoFee)}.`)
  if (report.debtIncurred > 0) details.push(`The shortfall of ${formatCr(report.debtIncurred)} is now owed to your insurer.`)
  if (report.cargoValue > 0) details.push(`Cargo in the wreck: about ${formatCr(report.cargoValue)}.`)
  const fleet = report.fleetShip
  if (fleet?.lost === true) {
    return { title: 'Ship lost', headline: `Your fleet ship is gone for good: it was uninsured. You are back in the lease ship at ${where}; ${cargo}.`, details }
  }
  const rebuilt = fleet?.insured === true ? `Insured, your fleet ship was rebuilt at ${where}` : `Rebuilt at ${where}`
  return {
    title: 'Ship lost',
    headline: `${rebuilt}: rebuild fee ${fee} ${how}; ${cargo}.`,
    details,
  }
}

export interface OwnershipModel {
  title: string
  subtitle: string
  /** "Paid off after 3 h 42 min of flight". */
  payoff: string
  details: string[]
}

export function ownershipModel(career: Career): OwnershipModel {
  const s = career.stats
  const details = [
    `Ore mined: ${formatTonnes(s.oreT)} t · parts secured: ${s.partsSecured} · earned: ${formatCr(s.earned)}.`,
    `Wallet: ${formatCr(career.wallet)}.`,
    'Insurance is now optional, at the Lease Office.',
  ]
  return {
    title: 'The Kestrel is yours',
    subtitle: 'Tethys Crew Finance has released the lien.',
    payoff: `Paid off after ${formatDuration(career.gameTime)} of flight`,
    details,
  }
}
